
export default {
    props: {
        collName: String,
        id: Number,
        validateFn: Function,
        postSaveFn: Function,
    },

    data() {
        return {
            item: null,
            editItem: null,
            isDirty: false,
            isLoaded: false,
            skipDirty: false,
        };
    },

    computed: {
        readonly() {
            return $.isImmutable(this.collName, this.item) || !!this.item?.archived;
        },
        fieldProps() {
            return {
                collName: this.collName,
                editItem: this.editItem,
                readonly: this.readonly,
                isDirty: this.isDirty,
            };
        },
    },

    watch: {
        editItem: {
            handler(val, oldVal) {
                // Mark as dirty if editItem stayed the same, but something changed within
                if (!this.skipDirty && val === oldVal) {
                    this.isDirty = true;
                }
            },
            deep: true,
        },
    },

    methods: {
        initEdit() {
            this.editItem = _.cloneDeep(this.item);
            this.isDirty = false;
        },
        async processRefs(op) {
            const refDefs = $.getExpandedRefDefs(this.collName);

            // Operating on a copy, because modifying the original instance could trigger
            // reactive updates which caused refs to be re-actualized, thus reverting
            // the work done by this method and causing schema validation failures on server.
            // Example: update rental date & save would re-evaluate Cases.getDisabledDates
            // which would re-actualize editItem.vehicle, which would then get sent to server
            const copy = _.cloneDeep(this.editItem);

            for (const [refName, { collName, fieldPath, isEmbedded, subItemsPrefix }] of Object.entries(refDefs)) {
                const ref = $.get(copy, refName);
                const val = $.get(copy, fieldPath);

                if (isEmbedded && val) {
                    if (op === 'create' && ref === 0) {
                        // Create ref item
                        const { id } = await $.createOne(collName, val);

                        $.set(copy, refName, id);
                    }

                    if (op === 'update') {
                        // Update ref item
                        await $.updateOne(collName, val);
                    }
                }

                if (subItemsPrefix) {
                    // Delete ref items in sub-items from main item
                    const idx = subItemsPrefix.length;
                    const inFieldPath = fieldPath.substr(idx + 1);
                    const subItems = $.get(copy, subItemsPrefix) || [];

                    for (const i of subItems) {
                        _.unset(i, inFieldPath);
                    }
                } else {
                    // Delete ref item from main item
                    _.unset(copy, fieldPath);
                }
            }

            return copy;
        },
        async create() {
            if (this.validateFn && !(await this.validateFn(null, this.editItem)))
                return;

            const copy = await this.processRefs('create');

            this.item = await $.createOne(this.collName, copy, true);

            // Quick hack to enable downloading PDF when generating
            // contract for new case without saving it beforehand
            if (this.collName === 'cases') {
                this.editItem.id = this.item.id;
                this.editItem.code = this.item.code;
                this.editItem.client = this.item.client;
            }

            this.initEdit();

            if (this.postSaveFn) {
                await this.postSaveFn(this.editItem, 'create');
            }

            notifyOk("Vytvoření proběhlo úspěšně.");

            this.$emit('create', this.item);
            this.$emit('init', this.item, this.editItem);
        },
        async update() {
            if (this.validateFn && !(await this.validateFn(this.item, this.editItem)))
                return;

            const copy = await this.processRefs('update');

            this.item = await $.updateOne(this.collName, copy, true);

            // Quick hack to enable selecting correct type based on client rs
            // when generating contract and downloading PDF for case
            if (this.collName === 'cases') {
                this.editItem.client = this.item.client;
            }

            this.initEdit();

            if (this.postSaveFn) {
                await this.postSaveFn(this.editItem, 'update');
            }

            notifyOk("Uložení proběhlo úspěšně.");

            this.$emit('update', this.item);
            this.$emit('init', this.item, this.editItem);
        },
        async revert() {
            this.initEdit();

            this.$emit('revert', this.item);
            this.$emit('init', this.item, this.editItem);
        },
        async reload() {
            this.item = await $.fetchOne(this.collName, this.id, true);

            this.initEdit();

            this.$emit('reload', this.item);
            this.$emit('init', this.item, this.editItem);
        },
        async archive() {
            const item = await $.safeArchiveOne(this.collName, this.item, true);

            if (!item)
                return;

            this.item = item;

            this.initEdit();

            if (this.postSaveFn) {
                await this.postSaveFn(this.editItem, 'archive');
            }

            notifyOk("Smazání proběhlo úspěšně.");

            this.$emit('archive', this.item);
            this.$emit('init', this.item, this.editItem);
        },
        async restore() {
            const item = await $.safeRestoreOne(this.collName, this.item, true);

            if (!item)
                return;

            this.item = item;

            this.initEdit();

            if (this.postSaveFn) {
                await this.postSaveFn(this.editItem, 'restore');
            }

            notifyOk("Obnovení proběhlo úspěšně.");

            this.$emit('restore', this.item);
            this.$emit('init', this.item, this.editItem);
        },
        async destroy() {
            if (!(await $.safeDestroyOne(this.collName, this.item)))
                return;

            notifyOk("Trvalé smazání proběhlo úspěšně.");

            this.$emit('destroy', this.item);
        },
    },

    @cancellableFetch
    async mounted() {
        if (this.loadItem?.()) {
            // Item loaded in a custom way
        } else if (!this.id) {
            // New item
            this.editItem = $.getNu(this.collName);

            // Fetch direct and indirect ref collections
            await $.actualizeAndFetch(this.collName, {}, true, true);
        } else {
            // Existing item
            this.item = await $.fetchOne(this.collName, this.id, true);

            this.initEdit();
        }

        this.isLoaded = true;

        this.$emit('init', this.item, this.editItem);
    },

    beforeDestroy() {
        this.$emit('closing', this.item, this.editItem);
    },
};
